import { mediaQueryLarge, startViewTransition } from '@theme/utilities';

/**
 * A custom element that renders a results list
 * Handles layout switching only (no infinite scroll)
 */
export default class ResultsList extends HTMLElement {
  constructor() {
    super();
    this.refs = {};
    this.initialized = false;
    this.#initializeRefs();
  }

  connectedCallback() {
    super.connectedCallback?.();
    
    if (mediaQueryLarge.addEventListener) {
      mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    }
    
    this.setAttribute('initialized', '');
    
    // Initialize grid layout
    this.#initializeGridLayout();
    
    this.initialized = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    
    if (mediaQueryLarge.removeEventListener) {
      mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    }
  }

  /**
   * Initialize all element refs
   */
  #initializeRefs() {
    const refElements = this.querySelectorAll('[ref]');
    refElements.forEach(element => {
      const refName = element.getAttribute('ref');
      if (refName) {
        this.refs[refName] = element;
      }
    });
  }

  /**
   * Initialize grid layout from sessionStorage
   */
  #initializeGridLayout() {
    const grid = this.refs.grid;
    if (!grid) return;
    
    try {
      const currentDevice = window.innerWidth >= 750 ? 'desktop' : 'mobile';
      const storedLayout = sessionStorage.getItem(`product-grid-view-${currentDevice}`);
      
      if (storedLayout && storedLayout !== 'default') {
        this.#setLayoutSafely(storedLayout);
        
        // Update radio buttons if they exist
        const radioButtons = document.querySelectorAll(`input[type="radio"][name="grid"][value="${storedLayout}"]`);
        if (radioButtons.length > 0) {
          radioButtons[0].checked = true;
        }
      }
    } catch (e) {
      // Silently fail
    }
  }

  /**
   * Updates the layout (called from radio buttons)
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  /**
   * Layout change with View Transition if supported
   * @param {string} value 
   */
  #animateLayoutChange(value) {
    const { grid } = this.refs;
    if (!grid) return;
    
    if (typeof startViewTransition === 'function') {
      startViewTransition(() => this.#setLayoutSafely(value), ['product-grid']);
    } else {
      this.#setLayoutSafely(value);
    }
    
    try {
      const currentDevice = window.innerWidth >= 750 ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${currentDevice}`, value);
    } catch (e) {
      // Silently fail
    }
  }

  /**
   * Safely set layout
   * @param {string} value 
   */
  #setLayoutSafely(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event
   * @param {MediaQueryListEvent} event 
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayoutSafely('default');
    } else {
      this.#setLayoutSafely('default');
    }
  };
}

// Register the custom element
if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}